import * as THREE from 'three/webgpu';
import { useGLTF, useTexture } from '@react-three/drei';
import { MeshSurfaceSampler, type GLTF } from 'three/examples/jsm/Addons.js';
import type { ThreeElements } from '@react-three/fiber';
import { useLayoutEffect, useMemo, useRef } from 'react';
import {
  attribute,
  color,
  hash,
  instancedBufferAttribute,
  instanceIndex,
  mix,
  positionLocal,
  positionWorld,
  smoothstep,
  texture,
  time,
  uniform,
  varying,
  vec2,
  vec3,
} from 'three/tsl';
import { windNoiseFunction } from '../utils/tsl-noise';
import { useControls } from 'leva';
import { StaticCollider } from 'bvhecctrl';
// import { RigidBody } from '@react-three/rapier';

type Islandprops = ThreeElements['group'] & {
  islandMesh: THREE.Mesh;
  islandmaterial: THREE.MeshStandardMaterial;
};
type DandiGLTFResult = GLTF & {
  nodes: {
    crown001_0_1: THREE.Mesh;
    crown001_0_2: THREE.Mesh;
  };
  materials: {
    ['Material.001']: THREE.MeshBasicMaterial;
    PinkFlower: THREE.MeshStandardMaterial;
  };
};

export function Island({ islandMesh, islandmaterial, ...props }: Islandprops) {
  const grassCount = 200000;

  const flowerCount = 150;

  const sunflowerCount = 50;

  const grassInstanceMeshRef = useRef<ThreeElements['instancedMesh']>(null!);
  const flowersInstanceMeshRef = useRef<ThreeElements['instancedMesh']>(null!);

  const geometryRef = useRef<THREE.BufferGeometry>(null!);
  const materialRef = useRef<ThreeElements['meshStandardNodeMaterial']>(null!);
  const flowerMaterialRef = useRef<ThreeElements['meshBasicNodeMaterial']>(
    null!
  );
  const sunflowerMaterialRef = useRef<ThreeElements['meshBasicNodeMaterial']>(
    null!
  );

  const sunflowersInstanceMeshRef = useRef<
    ThreeElements['meshBasicNodeMaterial']
  >(null!);

  //flowers
  const flowers = useGLTF(
    '/models/sunflower_dandi-transformed.glb'
  ) as unknown as DandiGLTFResult;

  const controls = useControls(
    'grass',
    {
      grassX: {
        value: 40,
        min: 10,
        max: 50,
      },
      grassY: {
        value: 40,
        min: 10,
        max: 50,
      },
      grassFloorColor: {
        value: '#4b3820',
        onChange: (v) => {
          uniforms.grassFloorColor.value = new THREE.Color(v);
        },
        transient: false,
      },
      // grassTipColor: {
      //   value: '#382d20',
      // },
      grassTipOne: {
        value: '#73ce32',
        onChange: (v) => {
          uniforms.grassTipOne.value = new THREE.Color(v);
        },
      },
      grassTipTwo: {
        value: '#000',
        onChange: (v) => {
          uniforms.grassTipTwo.value = new THREE.Color(v);
        },
      },
    },
    { collapsed: true }
  );

  //textures
  const [perlinNoiseTexture2] = useTexture(['/texture/noise/perlin2.png']);

  perlinNoiseTexture2.wrapS = perlinNoiseTexture2.wrapT = THREE.RepeatWrapping;

  const { geoPositionBufferAttribute, uvAttribute, index, tipAttribute } =
    useMemo(() => {
      const vertexCount = 3;
      const position = new Float32Array(vertexCount * 3);
      const uvs = new Float32Array(vertexCount * 2);
      const tip = new Float32Array([0, 1, 0]);
      const index = [0, 2, 1];

      for (let i = 0; i < vertexCount; i++) {
        const i3 = i * 3;
        const i2 = i * 2;

        position[i3] = (i - 1) * 0.07; // -0.5, 0, 0.5 for i = 0, 1, 2
        position[i3 + 1] = i === 1 ? 0.7 : 0; // 0, 1, 0 for i = 0, 1, 2
        position[i3 + 2] = 0; // all z at 0

        //genrate uv map
        uvs[i2] = i * 0.5; // 0, 0.5, 1 for i = 0, 1, 2
        uvs[i2 + 1] = i === 1 ? 1 : 0; // 0, 1, 0 for i = 0, 1, 2
      }

      const geoPositionBufferAttribute = new THREE.Float32BufferAttribute(
        position,
        3
      );
      const uvAttribute = new THREE.Float32BufferAttribute(uvs, 2);

      const tipAttribute = new THREE.Float32BufferAttribute(tip, 1);

      //update the geometry with its needed attributes

      return { geoPositionBufferAttribute, uvAttribute, index, tipAttribute };
    }, []);

  const uniforms = useMemo(() => {
    const color1 = uniform(color('#382d20'));
    const grassTipOne = uniform(color('#62ad2c'));
    const grassTipTwo = uniform(color('#000'));

    const grassFloorColor = uniform(color('#4b3820'));
    return {
      color1,
      grassTipOne,
      grassTipTwo,
      grassFloorColor,
    };
  }, []);

  /**Nodes */
  useLayoutEffect(() => {
    const sampler = new MeshSurfaceSampler(islandMesh)
      .setWeightAttribute('color')
      .build();

    const instancePositionArray = [];
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3(1, 1, 1);
    const flowerScale = new THREE.Vector3(1.7, 1.7, 1.7);
    const sunflowerScale = new THREE.Vector3(2, 2, 2);

    const normal = new THREE.Vector3();
    const yAxis = new THREE.Vector3(0, 1, 0);
    const matrix = new THREE.Matrix4();
    const flowerMatrix = new THREE.Matrix4();
    const sunflowerMatrix = new THREE.Matrix4();

    for (let i = 0; i < grassCount; i++) {
      sampler.sample(position, normal);

      quaternion.setFromUnitVectors(yAxis, normal);

      const randomRotation = new THREE.Euler(0, Math.random() * Math.PI * 2);

      const randomQuaternion = new THREE.Quaternion().setFromEuler(
        randomRotation
      );

      quaternion.multiply(randomQuaternion);
      instancePositionArray.push(position.x, position.y, position.z);
      matrix.compose(position, quaternion, scale);
      flowerMatrix.compose(position, quaternion, flowerScale);
      sunflowerMatrix.compose(position, quaternion, sunflowerScale);

      if (grassInstanceMeshRef.current) {
        //@ts-expect-error mat3 error
        grassInstanceMeshRef.current.setMatrixAt(i, matrix);
      }

      if (flowersInstanceMeshRef.current) {
        //@ts-expect-error mat3 error
        flowersInstanceMeshRef.current.setMatrixAt(i, flowerMatrix);
      }

      if (sunflowersInstanceMeshRef.current) {
        //@ts-expect-error mat3 error
        sunflowersInstanceMeshRef.current.setMatrixAt(i, sunflowerMatrix);
      }
    }

    const positionAttribute = instancedBufferAttribute(
      new THREE.InstancedBufferAttribute(
        new Float32Array(instancePositionArray),
        3
      )
    );

    const uGrassX = uniform(controls.grassX, 'float');
    const uGrassY = uniform(controls.grassY, 'float');

    const islandUV = positionAttribute.xz.div(vec2(uGrassX, uGrassY)).add(0.5);

    const mapping = islandmaterial.map as THREE.Texture;
    const terainTexture = texture(mapping, islandUV);

    //simulate shadows
    const noiseMapping = texture(
      perlinNoiseTexture2,
      islandUV.add(time.mul(0.05)).mul(0.9)
    );

    const redChannelIntensity = terainTexture.r;
    const visibility = smoothstep(0, 1, redChannelIntensity);

    const tipnessAttribute = varying(attribute('tipness'));

    //---- wind effect ------
    const wind = windNoiseFunction(perlinNoiseTexture2, positionAttribute);

    const randomness = hash(instanceIndex).add(0.5).mul(tipnessAttribute);
    const windIntensity = 4.0;
    materialRef.current.positionNode = positionLocal
      .add(
        vec3(
          wind.x.mul(randomness).mul(windIntensity),
          0,
          wind.z.mul(randomness).mul(windIntensity)
        )
      )
      .mul(vec3(visibility));

    flowerMaterialRef.current.positionNode = positionLocal
      .add(
        vec3(
          wind.x.mul(positionWorld.y).mul(windIntensity),
          0,
          wind.z.mul(positionWorld.y).mul(windIntensity)
        )
      )
      .mul(vec3(visibility));

    sunflowerMaterialRef.current.positionNode = positionLocal
      .add(
        vec3(
          wind.x.mul(positionWorld.y).mul(windIntensity),
          0,
          wind.z.mul(positionWorld.y).mul(windIntensity)
        )
      )
      .mul(vec3(visibility));

    //material color
    const grassTipColor = mix(
      uniforms.grassTipOne,
      uniforms.grassTipTwo,
      noiseMapping.r.mul(1.5)
    );

    const grassColor = mix(uniforms.color1, grassTipColor, tipnessAttribute);
    const finalMix = mix(uniforms.grassFloorColor, grassColor, 0.45);
    materialRef.current.colorNode = vec3(finalMix);

    // materialRef.current.outputNode = vec4(vec3(1.0, 0.3, 0.2), 1);
    materialRef.current.needsUpdate = true;
    flowerMaterialRef.current.needsUpdate = true;
    sunflowerMaterialRef.current.needsUpdate = true;
  }, [perlinNoiseTexture2, controls, islandMesh, islandmaterial, uniforms]);
  return (
    <>
      {/* <RigidBody type='fixed' colliders='trimesh'> */}
      <StaticCollider>
        <mesh {...props} geometry={islandMesh.geometry} receiveShadow>
          <meshPhongNodeMaterial color={controls.grassFloorColor} />
        </mesh>
      </StaticCollider>

      {/* </RigidBody> */}

      {/* grass */}
      <instancedMesh
        ref={grassInstanceMeshRef}
        args={[undefined, undefined, grassCount]}
        receiveShadow
      >
        {/* grass triangle */}
        <bufferGeometry
          ref={geometryRef}
          onUpdate={(self) => {
            self.computeVertexNormals();
            self.setIndex(index);
          }}
        >
          {/* positions */}
          <bufferAttribute
            args={[
              geoPositionBufferAttribute.array,
              geoPositionBufferAttribute.itemSize,
            ]}
            attach='attributes-position'
          />
          {/* uv */}
          <bufferAttribute
            args={[uvAttribute.array, uvAttribute.itemSize]}
            attach='attributes-uv'
          />
          {/* tipness */}
          <bufferAttribute
            args={[tipAttribute.array, tipAttribute.itemSize]}
            attach='attributes-tipness'
          />
        </bufferGeometry>

        <meshToonNodeMaterial ref={materialRef} side={THREE.DoubleSide} />
      </instancedMesh>

      <instancedMesh
        ref={flowersInstanceMeshRef}
        args={[undefined, undefined, flowerCount]}
        geometry={flowers.nodes.crown001_0_2.geometry}
      >
        <meshBasicNodeMaterial
          ref={flowerMaterialRef}
          map={flowers.materials.PinkFlower.map}
        />
      </instancedMesh>

      <instancedMesh
        ref={sunflowersInstanceMeshRef}
        args={[undefined, undefined, sunflowerCount]}
        geometry={flowers.nodes.crown001_0_1.geometry}
      >
        <meshBasicNodeMaterial
          ref={sunflowerMaterialRef}
          map={flowers.materials['Material.001'].map}
        />
      </instancedMesh>
    </>
  );
}
